// Reactive visuals (docs/07 §7.3) — a single rAF loop that sweeps the playhead
// and drives per-layer blooms from live meters, plus the master meter. Honors
// prefers-reduced-motion (playhead/meters stay; decorative blooms are dropped).

import type { Looper } from "../audio/looper.ts";
import type { TransportController } from "../audio/transport.ts";
import type { Lanes } from "../ui/lanes.ts";
import type { MixerStrip } from "../ui/mixer.ts";

export interface VisualizerDeps {
  transport: TransportController;
  looper: Looper;
  lanes: Lanes;
  mixerStrip: MixerStrip;
  getMasterLevel: () => number;
  reducedMotion: boolean;
}

export class Visualizer {
  private rafId = 0;
  private running = false;
  private readonly levels = new Map<string, number>();

  constructor(private readonly deps: VisualizerDeps) {}

  start(): void {
    if (this.running) return;
    this.running = true;
    this.rafId = requestAnimationFrame(this.tick);
  }

  stop(): void {
    this.running = false;
    if (this.rafId) cancelAnimationFrame(this.rafId);
    this.rafId = 0;
  }

  private readonly tick = (): void => {
    if (!this.running) return;
    const { transport, looper, lanes, mixerStrip, getMasterLevel, reducedMotion } = this.deps;

    lanes.setProgress(transport.progress);

    if (!reducedMotion) {
      this.levels.clear();
      for (const id of looper.layerIds()) {
        // Light smoothing so blooms decay instead of strobing.
        this.levels.set(id, looper.layerLevel(id));
      }
      lanes.setLevels(this.levels);
    }
    mixerStrip.setMasterLevel(getMasterLevel());

    this.rafId = requestAnimationFrame(this.tick);
  };
}
